// D:\MAHAL(UPDATED)-NEW11-04\MAHAL kdsagakhjqsdfk\src\pages\Support\AutomationRules.js

import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import axios from "axios";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

import {
  FaRobot,
  FaBolt,
  FaSyncAlt,
  FaExclamationTriangle,
  FaClock,
  FaLayerGroup,
  FaCheckCircle,
  FaPlay,
  FaPause,
  FaCogs,
  FaShieldAlt,
  FaChartLine,
  FaProjectDiagram,
  FaArrowUp,
  FaArrowDown,
  FaFire,
  FaUserClock,
  FaCodeBranch
} from "react-icons/fa";

// import "./AutomationRules.css";
import "./Support.css";

const API =
  "https://api.mahal.qa/api/v1/admin/support";

const CHART_COLORS = [
  "#e8f163",
  "#f59157",
  "#10b981",
  "#f59e0b",
  "#ef4444"
];

export default function AutomationRules() {

  const token =
    localStorage.getItem(
      "admin_token"
    );

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [tickets, setTickets] =
    useState([]);

  const [dashboard, setDashboard] =
    useState({});

  const [slaBreached, setSlaBreached] =
    useState([]);

  const [performance, setPerformance] =
    useState([]);

  const [admins, setAdmins] =
    useState([]);

  const [automationRules] =
    useState([
      {
        id: 1,
        name: "Auto Escalation Engine",
        description:
          "Escalates unresolved SLA tickets automatically",
        status: "active",
        executions: 942,
        success_rate: 98,
        impact: "critical"
      },

      {
        id: 2,
        name: "Priority Smart Routing",
        description:
          "Assigns high priority tickets dynamically",
        status: "active",
        executions: 621,
        success_rate: 96,
        impact: "high"
      },

      {
        id: 3,
        name: "Idle Ticket Monitor",
        description:
          "Detects inactive tickets & alerts admins",
        status: "active",
        executions: 312,
        success_rate: 92,
        impact: "medium"
      },

      {
        id: 4,
        name: "Ops Queue Failover",
        description:
          "Redirects overflow workload automatically",
        status: "paused",
        executions: 104,
        success_rate: 88,
        impact: "critical"
      }
    ]);

  /* ========================================================
     FETCH
  ======================================================== */

  const fetchAutomationData =
    async (manual = false) => {

      try {

        if (manual) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const headers = {
          Authorization: `Bearer ${token}`
        };

        const [
          dashboardRes,
          ticketsRes,
          slaRes,
          performanceRes,
          adminsRes
        ] = await Promise.all([

          axios.get(
            `${API}/dashboard`,
            { headers }
          ),

          axios.get(
            `${API}/tickets`,
            {
              headers,
              params: {
                limit: 300
              }
            }
          ),

          axios.get(
            `${API}/sla-breached`,
            { headers }
          ),

          axios.get(
            `${API}/admin-performance`,
            { headers }
          ),

          axios.get(
            `${API}/admins`,
            { headers }
          )

        ]);

        setDashboard(
          dashboardRes.data || {}
        );

        setTickets(
          ticketsRes.data?.data || []
        );

        setSlaBreached(
          slaRes.data || []
        );

        setPerformance(
          performanceRes.data || []
        );

        setAdmins(
          adminsRes.data || []
        );

      } catch (error) {

        console.error(
          "Automation dashboard failed",
          error
        );

      } finally {

        setLoading(false);
        setRefreshing(false);
      }
    };

  useEffect(() => {

    fetchAutomationData();

    const interval =
      setInterval(() => {

        fetchAutomationData();

      }, 2500000);

    return () =>
      clearInterval(interval);

  }, []);

  /* ========================================================
     ANALYTICS
  ======================================================== */

  const activeRules =
    automationRules.filter(
      (r) =>
        r.status === "active"
    );

  const pausedRules =
    automationRules.filter(
      (r) =>
        r.status === "paused"
    );

  const highPriorityTickets =
    tickets.filter(
      (t) =>
        t.priority === "high"
    );

  const escalatedTickets =
    tickets.filter(
      (t) =>
        (t.escalation_level || 0) > 0
    );

  const automationEfficiency =
    useMemo(() => {

      const totalExecutions =
        automationRules.reduce(
          (sum, rule) =>
            sum + rule.executions,
          0
        );

      const weighted =
        automationRules.reduce(
          (sum, rule) =>
            sum +
            rule.executions *
            rule.success_rate,
          0
        );

      return totalExecutions
        ? Math.round(
          weighted /
          totalExecutions
        )
        : 0;

    }, [automationRules]);

  const queueAnalytics =
    useMemo(() => {

      const grouped = {};

      tickets.forEach((ticket) => {

        const queue =
          ticket.queue_name ||
          "Unknown";

        grouped[queue] =
          (grouped[queue] || 0) + 1;

      });

      return Object.keys(grouped).map(
        (key) => ({
          name: key,
          value: grouped[key]
        })
      );

    }, [tickets]);

  const trendData =
    useMemo(() => {

      const grouped = {};

      tickets.forEach((ticket) => {

        const day =
          new Date(
            ticket.created_at
          ).toLocaleDateString();

        grouped[day] =
          (grouped[day] || 0) + 1;

      });

      return Object.keys(grouped)
        .slice(-7)
        .map((key) => ({
          day: key,
          tickets: grouped[key]
        }));

    }, [tickets]);

  const escalationFlow =
    useMemo(() => {

      const level1 =
        tickets.filter(
          (t) =>
            t.escalation_level === 1
        ).length;

      const level2 =
        tickets.filter(
          (t) =>
            t.escalation_level === 2
        ).length;

      const level3 =
        tickets.filter(
          (t) =>
            t.escalation_level >= 3
        ).length;

      return [
        {
          name: "L1",
          value: level1
        },
        {
          name: "L2",
          value: level2
        },
        {
          name: "L3",
          value: level3
        }
      ];

    }, [tickets]);

  const topAdmins =
    [...performance]
      .sort(
        (a, b) =>
          (b.resolved || 0) -
          (a.resolved || 0)
      )
      .slice(0, 5);

  /* ========================================================
     SUMMARY
  ======================================================== */

  const summaryCards = [

    {
      title: "Automation Rules",
      value:
        automationRules.length,
      icon: <FaRobot />,
      className: "primary"
    },

    {
      title: "Active Engines",
      value:
        activeRules.length,
      icon: <FaPlay />,
      className: "warning"
    },

    {
      title: "Paused Rules",
      value:
        pausedRules.length,
      icon: <FaPause />,
      className: "warning"
    },

    {
      title: "Escalated Tickets",
      value:
        escalatedTickets.length,
      icon:
        <FaExclamationTriangle />,
      className: "danger"
    },

    {
      title: "High Priority",
      value:
        highPriorityTickets.length,
      icon: <FaFire />,
      className: "critical"
    },

    {
      title: "Automation Accuracy",
      value:
        `${automationEfficiency}%`,
      icon: <FaShieldAlt />,
      className: "purple"
    }

  ];

  /* ========================================================
     LOADING
  ======================================================== */

  if (loading) {

    return (

      <div className="support-loader">

        <div className="support-loader-card">

          <div className="support-loader-ring"></div>

          <h2>
            Initializing Automation Core
          </h2>

          <p>
            Synchronizing enterprise
            workflow engines...
          </p>

        </div>

      </div>
    );
  }

  /* ========================================================
     RENDER
  ======================================================== */

  return (

    <div className="support-page">

      {/* ====================================================
         HEADER
      ==================================================== */}

      <div className="support-header">

        <div className="support-header-content">

          <div className="support-live-tag">

            <span className="support-live-dot"></span>

            LIVE AUTOMATION ENGINE

          </div>

          <h1>
            Workflow Automation Control
          </h1>

          <p>
            Intelligent routing,
            escalation orchestration,
            SLA automation & operational
            workflow management
          </p>

        </div>

        <button
          className={`support-refresh-btn ${refreshing
            ? "support-refreshing"
            : ""
            }`}
          onClick={() =>
            fetchAutomationData(
              true
            )
          }
        >

          <FaSyncAlt />



        </button>

      </div>

      <div className="support-summary-grid">

        {summaryCards.map(
          (card, index) => (

            <div
              key={index}
              className={`support-summary-card ${card.className}`}
            >

              <div className="support-summary-icon">
                {card.icon}
              </div>

              <div className="support-summary-meta">

                <h3>
                  {card.value}
                </h3>

                <p>
                  {card.title}
                </p>

              </div>

            </div>

          )
        )}

      </div>

      {/* ====================================================
         HERO GRID
      ==================================================== */}

      {/* <div className="support-automation-hero-grid">

        <div className="support-panel support-panel--accent">

          <div className="support-panel-header">

            <div>

              <span>
                AUTOMATION STATUS
              </span>

              <h2>
                {
                  automationEfficiency
                }
                %
              </h2>

            </div>

            <FaRobot />

          </div>

          <div className="support-health-progress">

            <div
              className="support-health-fill"
              style={{
                width: `${automationEfficiency}%`
              }}
            ></div>

          </div>

          <div className="support-health-metrics">

            <div>

              <strong>
                {
                  activeRules.length
                }
              </strong>

              <p>
                Active Engines
              </p>

            </div>

            <div>

              <strong>
                {
                  dashboard.total ||
                  0
                }
              </strong>

              <p>
                Ticket Volume
              </p>

            </div>

            <div>

              <strong>
                {
                  admins.length
                }
              </strong>

              <p>
                Admin Workforce
              </p>

            </div>

          </div>

        </div>



      </div> */}

      {/* ====================================================
         CHARTS
      ==================================================== */}

      <div className="support-chart-grid">

        {/* ================================================
           TREND
        ================================================ */}


        <div className="support-panel">

          {/* <div className="support-panel support-panel--accent"> */}

          <div className="support-panel-header">

            <div>

              <span>
                AUTOMATION STATUS
              </span>

              <h2>
                {
                  automationEfficiency
                }
                %
              </h2>

            </div>

            <FaRobot />

          </div>

          <div className="support-health-progress">

            <div
              className="support-health-fill"
              style={{
                width: `${automationEfficiency}%`
              }}
            ></div>

          </div>

          <div className="support-health-metrics">

            <div>

              <strong>
                {
                  activeRules.length
                }
              </strong>

              <p>
                Active Engines
              </p>

            </div>

            <div>

              <strong>
                {
                  dashboard.total ||
                  0
                }
              </strong>

              <p>
                Ticket Volume
              </p>

            </div>

            <div>

              <strong>
                {
                  admins.length
                }
              </strong>

              <p>
                Admin Workforce
              </p>

            </div>

          </div>

          {/* </div> */}



        </div>


        <div className="support-panel">

          <div className="support-panel-header">

            <div>

              <span>
                SYSTEM TRAFFIC
              </span>

              <h3>
                Workflow Traffic Trend
              </h3>

            </div>

            <FaChartLine />

          </div>

          <ResponsiveContainer
            width="100%"
            height={340}
          >

            <AreaChart
              data={trendData}
            >

              <defs>

                <linearGradient
                  id="automationGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >

                  <stop
                    offset="5%"
                    stopColor="#df962a"
                    stopOpacity={0.8}
                  />

                  <stop
                    offset="95%"
                    stopColor="#df962a"
                    stopOpacity={0}
                  />

                </linearGradient>

              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="day"
              />

              <YAxis />

              <Tooltip />

              <Area
                type="monotone"
                dataKey="tickets"
                stroke="#ddb224"
                fillOpacity={1}
                fill="url(#automationGradient)"
              />

            </AreaChart>

          </ResponsiveContainer>

        </div>

        {/* ================================================
           ESCALATION FLOW
        ================================================ */}

        {/* <div className="support-panel">

          <div className="support-panel-header">

            <div>

              <span>
                ESCALATION ENGINE
              </span>

              <h3>
                Escalation Pipeline
              </h3>

            </div>

            <FaCodeBranch />

          </div>

          <ResponsiveContainer
            width="100%"
            height={340}
          >

            <PieChart>

              <Pie
                data={
                  escalationFlow
                }
                dataKey="value"
                outerRadius={110}
                label
              >

                {escalationFlow.map(
                  (
                    item,
                    index
                  ) => (

                    <Cell
                      key={index}
                      fill={
                        CHART_COLORS[
                        index
                        ]
                      }
                    />

                  )
                )}

              </Pie>

              <Tooltip />

            </PieChart>

          </ResponsiveContainer>

        </div> */}

      </div>

      {/* ====================================================
         LOWER GRID
      ==================================================== */}

      <div className="support-two-col-grid">

        {/* ================================================
           QUEUE LOAD
        ================================================ */}

        <div className="support-panel">

          <div className="support-panel-header">

            <div>

              <span>
                ESCALATION ENGINE
              </span>

              <h3>
                Escalation Pipeline
              </h3>

            </div>

            <FaCodeBranch />

          </div>

          <ResponsiveContainer
            width="100%"
            height={340}
          >

            <PieChart>

              <Pie
                data={
                  escalationFlow
                }
                dataKey="value"
                outerRadius={110}
                label
              >

                {escalationFlow.map(
                  (
                    item,
                    index
                  ) => (

                    <Cell
                      key={index}
                      fill={
                        CHART_COLORS[
                        index
                        ]
                      }
                    />

                  )
                )}

              </Pie>

              <Tooltip />

            </PieChart>

          </ResponsiveContainer>

        </div>

        <div className="support-panel">

          <div className="support-panel-header">

            <div>

              <span>
                QUEUE ANALYTICS
              </span>

              <h3>
                Queue Workload Matrix
              </h3>

            </div>

            <FaLayerGroup />

          </div>

          <ResponsiveContainer
            width="100%"
            height={320}
          >

            <BarChart
              data={
                queueAnalytics
              }
            >

              <CartesianGrid
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="name"
              />

              <YAxis />

              <Tooltip />

              <Bar
                dataKey="value"
                fill="#df962a"
              />

            </BarChart>

          </ResponsiveContainer>

        </div>

        {/* ================================================
           TOP ADMINS
        ================================================ */}

        {/* <div className="support-panel">

          <div className="support-panel-header">

            <div>

              <span>
                WORKFORCE EFFICIENCY
              </span>

              <h3>
                Automation Assisted Admins
              </h3>

            </div>

            <FaUserClock />

          </div>

          <div className="support-top-list">

            {topAdmins.map(
              (
                admin,
                index
              ) => (

                <div
                  key={index}
                  className="support-top-row"
                >

                  <div className="support-user-info">

                    <div className="support-avatar">

                      <FaCogs />

                    </div>

                    <div>

                      <h4>
                        {
                          admin.name
                        }
                      </h4>

                      <p>
                        {
                          admin.queue_name
                        }
                      </p>

                    </div>

                  </div>

                  <div className="support-admin-metrics">

                    <div className="automation_metric success">

                      <FaCheckCircle />

                      {
                        admin.resolved
                      }

                    </div>

                    <div className="automation_metric info">

                      <FaClock />

                      {Number(
                        admin.avg_resolution_hours ||
                        0
                      ).toFixed(1)}
                      h

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        </div> */}

      </div>

      {/* ====================================================
         AUTOMATION RULES
      ==================================================== */}

      <div className="support-panel1">

        <div className="support-panel-header">

          <div>

            <span>
              WORKFORCE EFFICIENCY
            </span>

            <h3>
              Automation Assisted Admins
            </h3>

          </div>

          <FaUserClock />

        </div>

        <div className="support-top-list">

          {topAdmins.map(
            (
              admin,
              index
            ) => (

              <div
                key={index}
                className="support-top-row"
              >

                <div className="support-user-info">

                  <div className="support-avatar">

                    <FaCogs />

                  </div>

                  <div>

                    <h4>
                      {
                        admin.name
                      }
                    </h4>

                    <p>
                      {
                        admin.queue_name
                      }
                    </p>

                  </div>

                </div>

                <div className="support-admin-metrics">

                  <div className="support-kpi-pill">

                    <FaCheckCircle />

                    {admin.resolved}

                  </div>

                  <div className="support-kpi-pill">

                    <FaClock />

                    {Number(
                      admin.avg_resolution_hours || 0
                    ).toFixed(1)}
                    h

                  </div>

                </div>

              </div>

            )
          )}

        </div>

      </div>
      <div className="support-panel1">

        <div className="support-panel-header">

          <div>

            <span>
              AUTOMATION MATRIX
            </span>

            <h3>
              Intelligent Rule Engines
            </h3>

          </div>

          <FaProjectDiagram />

        </div>

        <div className="support-rule-grid">

          {automationRules.map(
            (rule) => (

              <div
                key={rule.id}
                className={`support-rule-card ${rule.impact}`}
              >

                <div className="support-rule-top">

                  <div className="support-rule-icon">

                    <FaBolt />

                  </div>

                  <div
                    className={`support-rule-status ${rule.status}`}
                  >

                    {rule.status}

                  </div>

                </div>

                <h3>
                  {rule.name}
                </h3>

                <p>
                  {
                    rule.description
                  }
                </p>

                <div className="support-rule-metrics">

                  <div>

                    <strong>
                      {
                        rule.executions
                      }
                    </strong>

                    <span>
                      Executions
                    </span>

                  </div>

                  <div>

                    <strong>
                      {
                        rule.success_rate
                      }
                      %
                    </strong>

                    <span>
                      Success
                    </span>

                  </div>

                </div>

              </div>

            )
          )}

        </div>

      </div>

      {/* ====================================================
         LIVE INCIDENTS
      ==================================================== */}

      <div className="support-panel1">

        <div className="support-panel-header">

          <div>

            <span>
              LIVE INCIDENT STREAM
            </span>

            <h3>
              Automation Trigger Queue
            </h3>

          </div>

          <FaExclamationTriangle />

        </div>

        <div className="support-table-wrap">

          <table className="support-table">

            <thead>

              <tr>

                <th>
                  Ticket
                </th>

                <th>
                  Queue
                </th>

                <th>
                  Priority
                </th>

                <th>
                  Escalation
                </th>

                <th>
                  Assigned
                </th>

                <th>
                  SLA Due
                </th>

              </tr>

            </thead>

            <tbody>

              {slaBreached
                .slice(0, 12)
                .map((ticket) => (

                  <tr
                    key={
                      ticket.ticket_id
                    }
                  >

                    <td>

                      <div className="support-table-title">

                        <strong>
                          #
                          {
                            ticket.ticket_id
                          }
                        </strong>

                        <span>
                          {
                            ticket.subject
                          }
                        </span>

                      </div>

                    </td>

                    <td>
                      {
                        ticket.queue_name ||
                        "-"
                      }
                    </td>

                    <td>

                      <span
                        className={`support-priority ${ticket.priority}`}
                      >

                        {
                          ticket.priority
                        }

                      </span>

                    </td>

                    <td>

                      <span className="support-badge is-critical">

                        Level{" "}
                        {
                          ticket.escalation_level
                        }

                      </span>

                    </td>

                    <td>

                      {ticket.assigned_admin_name ||
                        "Unassigned"}

                    </td>

                    <td>

                      {ticket.sla_due_at
                        ? new Date(
                          ticket.sla_due_at
                        ).toLocaleString()
                        : "-"}

                    </td>

                  </tr>

                ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}